import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  User, 
  Power,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { Logo } from './Logo';

// Custom SVG Icons
const DashboardIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className={className}>
    <path fillRule="evenodd" clipRule="evenodd" d="M14.3797 4.05063C13.0374 4.05063 11.9494 2.96251 11.9494 1.62025C11.9494 1.18287 12.0649 0.772479 12.2671 0.417927C12.0294 0.368679 11.7791 0.331405 11.5147 0.302767C10.5895 0.202522 9.42323 0.202522 7.93535 0.20253H7.86212C6.37424 0.202522 5.20799 0.202522 4.28272 0.302767C3.33848 0.40507 2.57342 0.617574 1.92019 1.09217C1.52473 1.37949 1.17696 1.72726 0.889641 2.12272C0.415044 2.77595 0.20254 3.54101 0.100237 4.48525C-8.00362e-06 5.41052 -7.95754e-06 6.57677 1.43726e-07 8.06457V8.13788C-7.95754e-06 9.62576 -8.00362e-06 10.792 0.100237 11.7173C0.20254 12.6615 0.415044 13.4266 0.889641 14.0798C1.17696 14.4753 1.52473 14.823 1.92019 15.1103C2.57342 15.585 3.33848 15.7975 4.28272 15.8998C5.20797 16 6.37424 16 7.86204 16H7.93543C9.42323 16 10.5895 16 11.5147 15.8998C12.459 15.7975 13.224 15.585 13.8773 15.1103C14.2727 14.823 14.6205 14.4753 14.9078 14.0798C15.3824 13.4266 15.5949 12.6615 15.6973 11.7173C15.7975 10.792 15.7975 9.62576 15.7975 8.13796V8.06457C15.7975 6.57677 15.7975 5.4105 15.6973 4.48525C15.6686 4.2209 15.6313 3.97062 15.5821 3.73288C15.2275 3.9351 14.8171 4.05063 14.3797 4.05063ZM12.217 5.24111C12.5182 5.3889 12.6427 5.75293 12.4949 6.0542L11.3118 8.46598C10.4834 10.1548 8.04861 10.0723 7.33634 8.33134C7.02112 7.56067 5.94335 7.52413 5.57665 8.27171L4.3936 10.6835C4.24582 10.9847 3.88179 11.1092 3.58052 10.9614C3.27925 10.8136 3.15482 10.4496 3.3026 10.1483L4.48565 7.73655C5.31407 6.04773 7.74886 6.1302 8.46112 7.87119C8.77634 8.64186 9.85414 8.6784 10.2208 7.93081L11.4038 5.51903C11.5517 5.21776 11.9157 5.09333 12.217 5.24111Z" fill="currentColor"/>
    <path d="M12.7595 1.62025C12.7595 2.51509 13.4849 3.24051 14.3798 3.24051C15.2746 3.24051 16 2.51509 16 1.62025C16 0.725412 15.2746 0 14.3798 0C13.4849 0 12.7595 0.725412 12.7595 1.62025Z" fill="currentColor"/>
  </svg>
);

const ProjectsIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 16 14" fill="none" className={className}>
    <path d="M15.9708 6.16354L14.913 11.4529C14.8129 11.952 14.543 12.4011 14.1493 12.7239C13.7556 13.0466 13.2623 13.2231 12.7532 13.2233H2.20389C1.61938 13.2233 1.05881 12.9911 0.645504 12.5778C0.232195 12.1645 0 11.6039 0 11.0194V2.20389C0 1.61938 0.232195 1.05881 0.645504 0.645504C1.05881 0.232195 1.61938 0 2.20389 0H5.14241C5.28642 0.00112459 5.42692 0.0445535 5.54645 0.124887L7.56668 1.46926H11.7541C12.3386 1.46926 12.8991 1.70145 13.3125 2.11476C13.7258 2.52807 13.958 3.08864 13.958 3.67315H12.4887C12.4887 3.47831 12.4113 3.29146 12.2735 3.15369C12.1358 3.01592 11.9489 2.93852 11.7541 2.93852H7.34629C7.20228 2.93739 7.06178 2.89396 6.94225 2.81363L4.92202 1.46926H2.20389C2.00905 1.46926 1.8222 1.54666 1.68443 1.68443C1.54666 1.8222 1.46926 2.00905 1.46926 2.20389V11.0194C1.46926 11.2143 1.54666 11.4011 1.68443 11.5389C1.8222 11.6767 2.00905 11.7541 2.20389 11.7541H2.33612L3.45276 6.17823C3.55288 5.67908 3.82272 5.22998 4.21644 4.90724C4.61016 4.5845 5.10347 4.40802 5.61257 4.40778H14.531C14.7486 4.40724 14.9637 4.45507 15.1606 4.54781C15.3576 4.64056 15.5314 4.7759 15.6696 4.94406C15.8069 5.11242 15.9051 5.30912 15.9571 5.51999C16.0092 5.73085 16.0139 5.95065 15.9708 6.16354Z" fill="currentColor"/>
  </svg>
);

const TestAIIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className={className}>
    <path d="M4.08063 8.00154C4.12045 8.96941 4.50963 9.92566 5.2486 10.6646C6.00899 11.4249 6.99927 11.8135 7.99567 11.8336C8.0404 12.9121 7.65215 14.0052 6.82868 14.8287C5.26659 16.3908 2.73354 16.3908 1.17145 14.8287C-0.390416 13.2666 -0.390552 10.7335 1.17145 9.17146C1.97293 8.36998 3.03033 7.98057 4.08063 8.00154ZM12.0718 8.00154C13.0715 8.01932 14.0659 8.40876 14.8287 9.17146C16.3908 10.7336 16.3908 13.2666 14.8287 14.8287C13.2666 16.3908 10.7335 16.3908 9.17145 14.8287C8.34839 14.0055 7.95906 12.9127 8.00348 11.8345C9.05138 11.8536 10.1052 11.4642 10.9049 10.6646C11.6438 9.92567 12.032 8.96939 12.0718 8.00154ZM8.00348 3.83748C7.95987 4.91464 8.34914 6.00625 9.17145 6.82869C9.97071 7.62794 11.0244 8.01724 12.0718 7.99861V8.00154C11.0244 7.98291 9.9707 8.37221 9.17145 9.17146C8.43257 9.91035 8.04337 10.8668 8.00348 11.8345C8.00087 11.8345 7.99828 11.8336 7.99567 11.8336C7.95556 10.8662 7.56731 9.91009 6.82868 9.17146C6.0681 8.41099 5.07728 8.02144 4.08063 8.00154C4.08058 8.00024 4.08069 7.99894 4.08063 7.99763C5.07736 7.97773 6.06807 7.58929 6.82868 6.82869C7.65116 6.00621 8.0393 4.91475 7.99567 3.83748C7.99828 3.83743 8.00088 3.83753 8.00348 3.83748ZM1.17145 1.17146C2.73347 -0.390554 5.26657 -0.3904 6.82868 1.17146C7.56829 1.91108 7.95644 2.86874 7.99567 3.83748C6.99931 3.85756 6.00898 4.24717 5.2486 5.0074C4.42647 5.82953 4.03739 6.9208 4.08063 7.99763C3.03033 8.01861 1.97294 7.63018 1.17145 6.82869C-0.390408 5.26658 -0.390557 2.73348 1.17145 1.17146ZM9.17145 1.17146C10.7335 -0.390552 13.2666 -0.39041 14.8287 1.17146C16.3908 2.73355 16.3908 5.2666 14.8287 6.82869C14.0658 7.59153 13.0715 7.98083 12.0718 7.99861C12.1154 6.92145 11.7272 5.82979 10.9049 5.0074C10.1052 4.20793 9.05132 3.81839 8.00348 3.83748C8.04271 2.86872 8.43182 1.91109 9.17145 1.17146Z" fill="currentColor"/>
  </svg>
);

const SettingsIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none" className={className}>
    <g clipPath="url(#clip0_4_106)">
      <path d="M0.823123 5.87125L1.99812 5.72187C2.095 5.42375 2.21437 5.13625 2.35375 4.86312L1.62812 3.92875C1.33437 3.55062 1.36875 3.01688 1.70437 2.69187L2.68875 1.7075C3.01687 1.36875 3.55062 1.335 3.92937 1.62812L4.8625 2.35375C5.13562 2.21438 5.42312 2.095 5.72187 1.99813L5.87125 0.825C5.9275 0.354375 6.32687 0 6.8 0H8.2C8.67312 0 9.0725 0.354375 9.12875 0.823125L9.27812 1.99813C9.57687 2.095 9.86437 2.21375 10.1375 2.35375L11.0712 1.62812C11.4487 1.335 11.9825 1.36875 12.3081 1.70438L13.2925 2.68813C13.6312 3.01688 13.6656 3.55063 13.3719 3.92938L12.6462 4.86312C12.7862 5.13625 12.905 5.42375 13.0019 5.72187L14.175 5.87125C14.6456 5.9275 15 6.32688 15 6.8V8.2C15 8.67313 14.6456 9.0725 14.1769 9.12875L13.0019 9.27812C12.905 9.57625 12.7856 9.86375 12.6462 10.1369L13.3719 11.0713C13.6656 11.4494 13.6312 11.9831 13.2956 12.3081L12.3112 13.2925C11.9825 13.6306 11.4494 13.6644 11.0706 13.3712L10.1369 12.6456C9.86375 12.7856 9.57625 12.905 9.27812 13.0013L9.12875 14.1737C9.0725 14.6456 8.67312 15 8.2 15H6.8C6.32687 15 5.9275 14.6456 5.87125 14.1769L5.72187 13.0019C5.42375 12.905 5.13625 12.7856 4.86312 12.6462L3.92875 13.3719C3.55062 13.6656 3.01687 13.6313 2.69187 13.2956L1.7075 12.3112C1.36875 11.9825 1.33437 11.4494 1.62812 11.0706L2.35375 10.1369C2.21375 9.86375 2.09437 9.57625 1.99812 9.27812L0.825624 9.12875C0.354998 9.0725 0.000623703 8.67313 0.000623703 8.2V6.8C-1.90735e-06 6.32688 0.354373 5.9275 0.823123 5.87125ZM7.5 10.625C9.22312 10.625 10.625 9.22313 10.625 7.5C10.625 5.77688 9.22312 4.375 7.5 4.375C5.77687 4.375 4.375 5.77688 4.375 7.5C4.375 9.22313 5.77687 10.625 7.5 10.625Z" fill="currentColor"/>
    </g>
    <defs>
      <clipPath id="clip0_4_106">
        <rect width="15" height="15" fill="white" transform="matrix(-1 0 0 1 15 0)"/>
      </clipPath>
    </defs>
  </svg>
);

type SidebarProps = {
  userName: string | null;
  setUserName?: (name: string | null) => void;
};

export type SidebarRef = {
  openSidebar: () => void;
};

const Sidebar = forwardRef<SidebarRef, SidebarProps>(function Sidebar({ userName, setUserName }, ref) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const menuItems = [
    { icon: DashboardIcon, label: 'Dashboard', path: '/dashboard' },
    { icon: ProjectsIcon, label: 'Projects', path: '/projects' },
    { icon: TestAIIcon, label: 'Test AI', path: '/test-ai' },
    { icon: SettingsIcon, label: 'Settings', path: '/settings' },
  ];

  // Check if screen is mobile/tablet (below 1024px)
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(true); // Auto-collapse on mobile (show icons only)
        setIsMobileOpen(false); // Not expanded/overlay by default
      } else {
        setIsMobileOpen(false); // Reset when switching to desktop
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const toggleSidebar = () => {
    if (isMobile) {
      // On mobile: toggle between collapsed (icons only) and expanded (overlay)
      setIsMobileOpen(!isMobileOpen);
    } else {
      // On desktop: toggle collapse/expand
      setIsCollapsed(!isCollapsed);
    }
  };

  const closeMobileSidebar = () => {
    setIsMobileOpen(false);
  };

  // Expose openSidebar method to parent component
  useImperativeHandle(ref, () => ({
    openSidebar: () => {
      if (isMobile) {
        setIsMobileOpen(true);
      }
    }
  }));

  const handleLogout = () => {
    localStorage.removeItem("userName");
    setUserName?.(null);
  };

  // تحديد أي عنصر active بناءً على المسار الحالي
  const getActiveItem = () => {
    if (location.pathname === '/test-ai') return 'Test AI';
    if (location.pathname === '/dashboard') return 'Dashboard';
    return null;
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isMobile && isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeMobileSidebar}
        />
      )}

      <div 
        className={`sidebarContainer h-screen bg-gray-100 flex flex-col transition-all duration-300 ${
          isMobile 
            ? `fixed lg:relative ${isMobileOpen ? 'z-50' : 'z-30'} translate-x-0 lg:translate-x-0 ${isMobileOpen ? 'w-64' : 'w-20'} lg:w-64`
            : isCollapsed ? 'w-20' : 'w-64'
        }`}
        data-mobile-expanded={isMobile && isMobileOpen ? 'true' : 'false'}
      >
        {/* Logo Section with Toggle Button */}
        <div className="p-6 flex items-center gap-3 relative logoContainer">
          {(!isCollapsed || (isMobile && isMobileOpen)) && (
            <div className="mb-8 authLogo">
              <Logo showText={true} />
            </div>
          )}
          {(isCollapsed && !isMobile) || (isMobile && !isMobileOpen) ? (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto">
              <Logo showText={false} />
            </div>
          ) : null}
          <button
            onClick={toggleSidebar}
            className="sidebarToggle absolute top-6 right-2 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
            aria-label="Toggle sidebar"
          >
            {isMobile ? (
              isMobileOpen ? (
                <X className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )
            ) : isCollapsed ? (
              <ChevronRight className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            )}
          </button>
        </div>

      {/* Navigation Links */}
      <nav className="flex-1 px-4 py-2">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = getActiveItem() === item.label;
          
          return (
            <Link
              key={item.label}
              to={item.path}
              onClick={isMobile ? closeMobileSidebar : undefined}
              className={`flex items-center gap-3 px-4 py-3 mb-2 rounded-lg transition-colors ${
                isActive
                  ? 'ActiveSidebarItem'
                  : 'text-gray-700 hover:bg-gray-200 sidebarItem'
              }`}
              title={(isCollapsed && !isMobile) || (isMobile && !isMobileOpen) ? item.label : undefined}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {(!isCollapsed && !isMobile) || (isMobile && isMobileOpen) ? <span className="font-medium">{item.label}</span> : null}
            </Link>
          );
        })}
      </nav>

      {/* User Section */}
      <div className="sidebarUserContainer">
        <div className="mb-4 sidebarUserItem">
          <div className={`userAvatar p-4 flex items-center gap-3 mb-3 ${(isCollapsed && !isMobile) ? 'justify-center' : ''}`}>
            <div className="userAvatarIcon  flex items-center justify-center text-white font-semibold flex-shrink-0">
              {userName ? userName.charAt(0).toUpperCase() : 'J'}
            </div>
            {((!isCollapsed && !isMobile) || (isMobile && isMobileOpen)) && (
              <span className="font-semibold text-gray-800 truncate">
                {userName || 'John Daniel'}
              </span>
            )}
          </div>
          
          <Link
            to="/profile"
            onClick={isMobile && isMobileOpen ? closeMobileSidebar : undefined}
            className={`p-4 sidebarUserItemLink flex items-center gap-3 text-gray-700 hover:text-gray-900 mb-2 ${((isCollapsed && !isMobile) || (isMobile && !isMobileOpen)) ? 'justify-center' : ''}`}
            title={((isCollapsed && !isMobile) || (isMobile && !isMobileOpen)) ? 'Profile Settings' : undefined}
          >
            <User className="w-4 h-4 flex-shrink-0" />
            {((!isCollapsed && !isMobile) || (isMobile && isMobileOpen)) && <span className="">Profile Settings</span>}
          </Link>
          
          <Link
            to="/"
            onClick={(e) => {
              if (isMobile && isMobileOpen) closeMobileSidebar();
              handleLogout();
            }}
            className={`p-4 sidebarUserItemLink flex items-center gap-3 text-gray-700 hover:text-gray-900 ${((isCollapsed && !isMobile) || (isMobile && !isMobileOpen)) ? 'justify-center' : ''}`}
            title={((isCollapsed && !isMobile) || (isMobile && !isMobileOpen)) ? 'Logout' : undefined}
          >
            <Power className="w-4 h-4 flex-shrink-0" />
            {((!isCollapsed && !isMobile) || (isMobile && isMobileOpen)) && <span className="">Logout</span>}
          </Link>
        </div>

        {/* Version */}
        {((!isCollapsed && !isMobile) || (isMobile && isMobileOpen)) && (
          <div className="text-center text-xs text-gray-500">
            Version 1.0
          </div>
        )}
      </div>
      </div>
    </>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;

